import React, { useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { App as AntdApp } from 'antd';
import AuthGuard from 'components/AuthGuard';
import { LoginGuard } from 'components/AuthGuard';
import PortalLayout from 'components/PortalLayout';
import LoginPage from 'pages/LoginPage';
import ChangePasswordPage from 'pages/ChangePasswordPage';
import NoPermissionPage from 'pages/NoPermissionPage';
import BalancePage from 'pages/BalancePage';
import UsageRecordsPage from 'pages/UsageRecordsPage';
import OrderCancelPage from 'pages/OrderCancelPage';
import BillListPage from 'pages/BillListPage';
import BillDetailPage from 'pages/BillDetailPage';
import WeighingQueryPage from 'pages/WeighingQueryPage';
import OrderQueryPage from 'pages/OrderQueryPage';
import OrderDetailPage from 'pages/OrderDetailPage';
import OrderExportTaskListPage from 'pages/OrderExportTaskListPage';
import NotFoundPage from 'pages/NotFoundPage';
import { OrderSelectionProvider } from 'components/OrderSelectionContext';
import { setMessageApi } from 'utils/messageHolder';
import { hasSettlementAccount, SETTLEMENT_RESTRICTED_PATHS } from 'utils/auth';

/**
 * 路由注册表：resource_path → 页面组件
 * key 与后端 customer_permission 表的 resource_path 字段一一对应
 */
export const routeMap: Record<string, React.FC> = {
    '/balance': BalancePage,
    '/usage-records': UsageRecordsPage,
    '/order-cancel': OrderCancelPage,
    '/weighing-query': WeighingQueryPage,
    '/bill-list': BillListPage,
    '/order-query': OrderQueryPage,
};

const App: React.FC = () => {
    const { message } = AntdApp.useApp();

    useEffect(() => {
        setMessageApi(message);
    }, [message]);

    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/no-permission" element={<NoPermissionPage />} />
            <Route element={<LoginGuard />}>
                <Route element={<PortalLayout />}>
                    <Route path="/change-password" element={<ChangePasswordPage />} />
                </Route>
            </Route>
            <Route element={<AuthGuard />}>
                {/*
                  * 订单勾选态挂在 PortalLayout 这一层，而不是 OrderQueryPage 内部：
                  * /order-query 与 /order-detail/:trackingNumber 是平级路由，点运单号进详情会卸载列表页，
                  * 状态放页面里就死在这一跳上——客户勾了几十单、点开一单核对、回来勾全没了，
                  * 而同页的筛选条件与页码是特意用 sessionStorage 保住的，两者行为会割裂。
                  * 布局路由在门户内部导航时不重新挂载，所以放这里刚好覆盖这趟往返。
                  */}
                <Route
                    element={
                        <OrderSelectionProvider>
                            <PortalLayout />
                        </OrderSelectionProvider>
                    }
                >
                    {/*
                      * 根路径不再硬跳 /balance 或 /order-cancel：运营查询角色两者都无权限，
                      * 硬跳会让 OrderCancelPage 在权限尚未加载完时先发一次请求、弹出无权限报错，再被 AuthGuard 重定向。
                      * 这里只占位，实际跳转由 PortalLayout 在菜单树加载完成后 navigate(findFirstLeafPath(menuTree)) 完成，
                      * 该逻辑同时已按 hasSettlementAccount 过滤过余额 / 使用记录入口。
                      */}
                    <Route path="/" element={<div />} />
                    {Object.entries(routeMap).map(([path, Component]) => {
                        const restricted = SETTLEMENT_RESTRICTED_PATHS.includes(path);
                        const Element: React.FC = () =>
                            restricted && !hasSettlementAccount() ? <NotFoundPage /> : <Component />;
                        return <Route key={path} path={path} element={<Element />} />;
                    })}
                    <Route path="/bill-detail/:billId" element={<BillDetailPage />} />
                    <Route path="/order-detail/:trackingNumber" element={<OrderDetailPage />} />
                    {/*
                      * 导出记录页必须作为独立 Route 注册、不进 routeMap：
                      * AuthGuard 对 routeMap 里的路径是按 `portal:${pathname.slice(1)}` 反推权限码的，
                      * 而本页复用的 portal:order-query:export 带冒号，反推只会得到不存在的
                      * portal:order-export-records —— 持有权限的账号也会被 redirect 走，页面永远打不开。
                      * 权限改由 AuthGuard 里的显式判断承担，与 /bill-detail、/order-detail 同一处理方式。
                      */}
                    <Route path="/order-export-records" element={<OrderExportTaskListPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                </Route>
            </Route>
        </Routes>
    );
};

export default App;
